/*
 * Copyright (c) 2021 WIT Software. All rights reserved.
 *
 * WIT Software Confidential and Proprietary information. It is strictly forbidden for 3rd parties to modify, decompile,
 * disassemble, defeat, disable or circumvent any protection mechanism; to sell, license, lease, rent, redistribute or
 * make accessible to any third party, whether for profit or without charge.
 *
 */

import React from 'react';
import { Link } from 'react-router-dom';
import { ProgressBar } from '.';

export const enum ButtonType {
  ANCHOR = 'anchor',
  BUTTON = 'button',
  SUBMIT = 'submit',
  LI = 'li',
  LINK = 'link',
  TAB = 'tab',
}

interface Props {
  bemBlock: string;
  bemModifier: string | number;
  buttonType: ButtonType;
  text: string;
  disabled?: boolean;
  active?: boolean;
  onClick?: () => void;
  className?: string;
  linkPath?: string;
  progressPercentage?: number;
  linkReplace?: boolean;
  onMouseOver?: () => void;
  onMouseLeave?: () => void;
}
/**
 *
 * @param buttonType type of tag used to build a button
 * @param text text inside the button
 * @param disabled toggle the disable option and style
 * @param onClick action which will be triggered when the button is pressed
 * @param className add one or more custom css classes to the button
 * @param linkPath only usable when 'link' is selected as a button type
 * @param progressPercentage only usable to show the content progress bar
 * @returns pre-styled generic button
 */
export default function Button({
  bemBlock,
  bemModifier,
  buttonType,
  text,
  disabled,
  active,
  onClick,
  className,
  linkPath = '',
  progressPercentage,
  linkReplace = false,
  onMouseOver,
  onMouseLeave,
}: Props): JSX.Element | null {
  const isTab = buttonType === ButtonType.TAB;
  const btnClasses = `btn ${isTab ? 'tab-btn' : 'gradient-btn'}`;
  const classNames = `${btnClasses} ${className ?? ''} ${disabled ? 'disabled' : ''} ${active ? 'active' : ''}`;
  const buttonId = `${bemBlock}_button___${bemModifier}`;
  const wrapperId = `${bemBlock}_buttonWrapper___${bemModifier}`;

  const handleOnClick = () => {
    if (onClick) {
      onClick();
    }
  };

  let buttonElement;

  switch (buttonType) {
    case ButtonType.ANCHOR:
      buttonElement = (
        <a id={buttonId} className={classNames} onClick={() => handleOnClick()}>
          <span>{text}</span>
        </a>
      );
      break;
    case ButtonType.BUTTON:
    case ButtonType.SUBMIT:
      buttonElement = (
        <button
          id={buttonId}
          type={buttonType === ButtonType.SUBMIT ? 'submit' : 'button'}
          className={classNames}
          disabled={disabled}
          onClick={() => handleOnClick()}
        >
          <span>{text}</span>
        </button>
      );
      break;
    case ButtonType.LI:
      buttonElement = (
        <li id={buttonId} className={classNames} onClick={() => handleOnClick()}>
          <span>{text}</span>
        </li>
      );
      break;
    case ButtonType.LINK:
      buttonElement = (
        <Link
          id={buttonId}
          className={classNames}
          to={linkPath}
          onClick={(ev) => (disabled ? ev.preventDefault() : undefined)}
          replace={linkReplace}
        >
          <span>{text}</span>
        </Link>
      );
      break;
    case ButtonType.TAB:
      return (
        <a id={buttonId} className={classNames} onClick={() => handleOnClick()}>
          <span>{text}</span>
        </a>
      );
    default:
      return null;
  }

  return (
    <>
      {buttonType === ButtonType.LI ? (
        buttonElement
      ) : (
        <div className="btn-wrapper" onMouseOver={onMouseOver} onMouseLeave={onMouseLeave} id={wrapperId}>
          {buttonElement}
          {progressPercentage ? (
            <ProgressBar percentage={progressPercentage} isActive bottomRounded bemModifier={bemModifier} />
          ) : null}
        </div>
      )}
    </>
  );
}
